import { DOCUMENT_ACCEPT, MESSAGE_ATTACHMENTS_MAX, MESSAGE_BODY_MAX, type PortalCaseDto, type PortalMessageDto } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Card, FileButton, Group, Paper, Select, Stack, Text, Textarea } from '@mantine/core';
import { IconPaperclip, IconSend, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, apiForm, errorText } from '../../api';
import { formatDateTime } from '../../format';

/** Strefa klienta → Wiadomości (docs/20): jedna rozmowa z biurem; przy wiadomości można wskazać sprawę, której dotyczy. */
export function PortalMessages({ base, cases = [] }: { base: string; cases?: PortalCaseDto[] }) {
  const qc = useQueryClient();
  const key = ['public-portal-messages', base];
  const q = useQuery({ queryKey: key, queryFn: () => api<PortalMessageDto[]>(`${base}/wiadomosci`), refetchInterval: 60_000, refetchOnWindowFocus: true });
  // klient widzi rozmowę — nowe odpowiedzi biura przeczytane (znacznik przy zakładce znika; „Nowa” zostaje do odświeżenia listy)
  const hasUnread = !!q.data?.some((m) => m.unread);
  useEffect(() => {
    if (!hasUnread) return;
    void api(`${base}/wiadomosci/przeczytane`, { method: 'POST' })
      .then(() => qc.invalidateQueries({ queryKey: ['public-portal'] }))
      .catch(() => undefined);
  }, [hasUnread, q.dataUpdatedAt, base, qc]);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  // jedna sprawa — wiadomość od razu w tej sprawie; kilka — wybór (domyślnie ogólna)
  const [caseId, setCaseId] = useState<string | null>(cases.length === 1 ? cases[0]!.id : null);
  const send = useMutation({
    mutationFn: () => {
      const f = new FormData();
      f.append('body', body.trim());
      if (caseId) f.append('caseId', caseId);
      files.forEach((x) => f.append('files', x));
      return apiForm<PortalMessageDto[]>(`${base}/wiadomosci`, f);
    },
    onSuccess: (list) => {
      qc.setQueryData(key, list);
      setBody('');
      setFiles([]);
      setError(null);
    },
    onError: (e) => setError(errorText(e)),
  });

  return (
    <Stack>
      <Text size="sm" c="dimmed">
        Tu możesz napisać do nas w sprawie prac — odpowiemy w tym miejscu, a o odpowiedzi powiadomimy e-mailem.
      </Text>
      {q.error && <Alert color="red">{errorText(q.error)}</Alert>}
      {q.data && !q.data.length && (
        <Card withBorder radius="md">
          <Text size="sm" c="dimmed">
            Nie ma jeszcze wiadomości.
          </Text>
        </Card>
      )}
      {q.data?.map((m) => (
        <Paper
          key={m.id}
          withBorder
          radius="md"
          p="sm"
          maw="85%"
          ml={m.direction === 'in' ? 'auto' : undefined}
          bg={m.direction === 'in' ? 'var(--app-tint-blue, var(--mantine-color-blue-light))' : undefined}
        >
          <Group gap={6} mb={4}>
            <Text size="xs" c="dimmed">
              {m.direction === 'in' ? 'Ty' : m.author} · {formatDateTime(m.at)}
            </Text>
            {m.unread && (
              <Badge size="xs" color="red">
                Nowa
              </Badge>
            )}
            {m.caseNumber && cases.length > 1 && (
              <Badge size="xs" variant="outline" color="gray">
                {m.caseNumber}
              </Badge>
            )}
          </Group>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {m.body}
          </Text>
          {m.attachments.map((a) => (
            <Anchor key={a.id} href={`/api${base}/wiadomosci/zalaczniki/${a.id}`} size="sm" display="block" mt={4}>
              <IconPaperclip size={12} /> {a.fileName}
            </Anchor>
          ))}
        </Paper>
      ))}
      <Card withBorder radius="md">
        <Stack gap="xs">
          {cases.length > 1 && (
            <Select
              label="Dotyczy sprawy"
              value={caseId ?? ''}
              onChange={(v) => setCaseId(v || null)}
              allowDeselect={false}
              data={[{ value: '', label: 'Ogólne (bez sprawy)' }, ...cases.map((c) => ({ value: c.id, label: `${c.number} — ${c.title}` }))]}
            />
          )}
          <Textarea
            label="Nowa wiadomość"
            autosize
            minRows={3}
            maxLength={MESSAGE_BODY_MAX}
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
          />
          {files.map((f, i) => (
            <Group key={`${f.name}-${i}`} gap={4}>
              <IconPaperclip size={12} />
              <Text size="xs">{f.name}</Text>
              <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="Usuń załącznik">
                <IconX size={12} />
              </Button>
            </Group>
          ))}
          {error && <Alert color="red">{error}</Alert>}
          <Group justify="space-between">
            <FileButton multiple accept={DOCUMENT_ACCEPT} onChange={(fs) => setFiles([...files, ...fs].slice(0, MESSAGE_ATTACHMENTS_MAX))}>
              {(props) => (
                <Button {...props} variant="default" size="xs" leftSection={<IconPaperclip size={14} />}>
                  Dołącz plik
                </Button>
              )}
            </FileButton>
            <Button leftSection={<IconSend size={14} />} disabled={!body.trim()} loading={send.isPending} onClick={() => send.mutate()}>
              Wyślij
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
